// The words a recipe is read by, in every language the importer knows — one
// list for both readers: the PWA (ingredient lines in lib/recipe.ts, step
// timers in lib/stepMinutes.ts) and the `recipe` function (section headings,
// servings and timers in free text), which gets this file through
// `make shared-lib`. Pure data and the patterns built from it; no imports, so
// the function's copy stays a plain copy.
//
// A new language is a row in each list here, never a pattern somewhere else.

// ── units ─────────────────────────────────────────────────────────────────────

// Canonical unit ← every spelling/plural we accept. Amounts only merge when
// their canonical units match; we deliberately don't convert across units
// (dl↔l, cup↔dl), surfacing that as a conflict instead. Units are normalised,
// never translated: "msk" stays "msk" and "tbsp" stays "tbsp" on the list, and
// the canonical form is the singular ("3 clove" is what a scaled English line
// prints — unlovely but unambiguous).
export const UNIT_SPELLINGS: Record<string, string> = {
  // metric — shared
  ml: 'ml', cl: 'cl', dl: 'dl', l: 'l', liter: 'l', litre: 'l', lit: 'l',
  g: 'g', gram: 'g', grams: 'g', hg: 'hg', kg: 'kg', kilo: 'kg',
  // Swedish
  krm: 'krm', tsk: 'tsk', msk: 'msk',
  st: 'st', styck: 'st', stycken: 'st', stk: 'st',
  port: 'port', portion: 'port', portioner: 'port',
  paket: 'paket', förp: 'paket', förpackning: 'paket', pkt: 'paket',
  burk: 'burk', burkar: 'burk',
  påse: 'påse', påsar: 'påse',
  flaska: 'flaska', flaskor: 'flaska',
  knippe: 'knippe', knippen: 'knippe',
  klyfta: 'klyfta', klyftor: 'klyfta',
  nypa: 'nypa', nypor: 'nypa',
  näve: 'näve', nävar: 'näve',
  skiva: 'skiva', skivor: 'skiva',
  // English
  cup: 'cup', cups: 'cup',
  tbsp: 'tbsp', tbs: 'tbsp', tablespoon: 'tbsp', tablespoons: 'tbsp',
  tsp: 'tsp', teaspoon: 'tsp', teaspoons: 'tsp',
  oz: 'oz', ounce: 'oz', ounces: 'oz',
  'fl oz': 'fl oz', 'fl. oz': 'fl oz',
  lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
  pinch: 'pinch', pinches: 'pinch',
  clove: 'clove', cloves: 'clove',
  can: 'can', cans: 'can', tin: 'can', tins: 'can',
  jar: 'jar', jars: 'jar',
  pack: 'pack', packs: 'pack', pkg: 'pack', package: 'pack', packages: 'pack',
  bunch: 'bunch', bunches: 'bunch',
  slice: 'slice', slices: 'slice',
  handful: 'handful', handfuls: 'handful',
  pc: 'pcs', pcs: 'pcs', piece: 'pcs', pieces: 'pcs',
  sprig: 'sprig', sprigs: 'sprig',
  stalk: 'stalk', stalks: 'stalk', stick: 'stalk', sticks: 'stalk',
  // German — kept as written too ("EL" is "el", not "msk")
  el: 'el', tl: 'tl', prise: 'prise', prisen: 'prise',
  stück: 'st',
  bund: 'bund', bunde: 'bund',
  dose: 'dose', dosen: 'dose',
  zehe: 'zehe', zehen: 'zehe',
  packung: 'packung', packungen: 'packung', pck: 'packung',
  becher: 'becher', handvoll: 'handvoll', block: 'block', scheibe: 'scheibe', scheiben: 'scheibe',
  // Norwegian and Danish — "ss" and "spsk" as written, like "msk"
  ss: 'ss', spsk: 'spsk', ts: 'ts', tesk: 'ts', teskje: 'ts', ssk: 'ss', spiseskje: 'ss',
  fedd: 'fedd', fed: 'fed', neve: 'neve', håndfull: 'neve', håndfuld: 'håndfuld',
  pakke: 'pakke', pakker: 'pakke', pk: 'pakke', boks: 'boks', bokser: 'boks', dåse: 'dåse', dåser: 'dåse', ds: 'dåse',
  bunt: 'bunt', bundt: 'bundt', skive: 'skive', skiver: 'skive', klype: 'klype', knivspiss: 'knivspiss', knsp: 'knivspiss',
  // French
  cs: 'cs', 'c.à.s': 'cs', càs: 'cs', 'c. à soupe': 'cs', 'c. à s.': 'cs', 'cuillère à soupe': 'cs', 'cuillères à soupe': 'cs',
  cc: 'cc', 'c.à.c': 'cc', càc: 'cc', 'c. à café': 'cc', 'c. à c.': 'cc', 'cuillère à café': 'cc', 'cuillères à café': 'cc',
  gousse: 'gousse', gousses: 'gousse', pincée: 'pincée', pincées: 'pincée', sachet: 'sachet', sachets: 'sachet',
  boîte: 'boîte', boîtes: 'boîte', botte: 'botte', bottes: 'botte', tranche: 'tranche', tranches: 'tranche',
  brin: 'brin', brins: 'brin', poignée: 'poignée', poignées: 'poignée',
  // Italian
  cucchiaio: 'cucchiaio', cucchiai: 'cucchiaio', cucchiaino: 'cucchiaino', cucchiaini: 'cucchiaino',
  spicchio: 'spicchio', spicchi: 'spicchio', pizzico: 'pizzico', pizzichi: 'pizzico', bustina: 'bustina', bustine: 'bustina',
  barattolo: 'barattolo', barattoli: 'barattolo', mazzetto: 'mazzetto', mazzetti: 'mazzetto', fetta: 'fetta', fette: 'fetta',
  rametto: 'rametto', rametti: 'rametto', manciata: 'manciata',
  // Spanish
  cucharada: 'cucharada', cucharadas: 'cucharada', cucharadita: 'cucharadita', cucharaditas: 'cucharadita',
  diente: 'diente', dientes: 'diente', pizca: 'pizca', pizcas: 'pizca', sobre: 'sobre', sobres: 'sobre',
  lata: 'lata', latas: 'lata', manojo: 'manojo', manojos: 'manojo', rebanada: 'rebanada', rebanadas: 'rebanada',
  rodaja: 'rodaja', rodajas: 'rodaja', puñado: 'puñado', puñados: 'puñado', taza: 'taza', tazas: 'taza',
}

// ── words around an amount (ingredient lines) ─────────────────────────────────

/** "X eller Y": a choice between two things. */
export const ALTERNATIVE_WORDS = ['eller', 'or', 'oder', 'ou', 'o', 'oppure']
/** "salt och peppar": two things on one line. */
export const PART_WORDS = ['och', 'and', 'und', '&', 'og', 'et', 'e', 'y']
/** "2 to 3", "2 à 3": a range between two amounts (a dash needs no word). */
export const RANGE_WORDS = ['to', 'till', 'or', 'eller', 'bis', 'til', 'à', 'a', 'o', 'ou']
/** "a pinch", "en gul lök", "eine Prise": an article at the start means one. */
export const ARTICLE_WORDS = ['a', 'an', 'en', 'ett', 'ein', 'eine', 'einen', 'ei', 'et', 'un', 'une', 'uno', 'una']
/** "1 cup of flour": the "of" belongs to the unit. */
export const OF_WORDS = ['of', 'av', 'von', 'af', 'de', 'di']
/** "1 c. à soupe d'huile": the "of" that leans on the next word. */
export const ELIDED_OF_WORDS = ["d'", 'd’']
/** "ca. 200 g", "about 2 cups": an approximate amount is still the amount. */
export const ABOUT_WORDS = [
  'ca.', 'ca', 'cirka', 'circa', 'etwa', 'about', 'approx.', 'approx', 'omkring', 'environ', 'aproximadamente', 'aprox.', 'aprox',
]
/** "Juice of 1 lemon": the thing to buy is the lemon. */
export const YIELD_WORDS = [
  'juice', 'zest', 'rind', 'peel', 'saft', 'saften', 'skal', 'skalet', 'rivet skal', 'finrivet skal',
  'jus', 'zeste', 'succo', 'scorza', 'zumo', 'jugo', 'ralladura',
]
export const YIELD_LINKS = ['of', 'from', 'av', 'fran', 'från', 'af', 'de', 'di']

// ── free text: sections, servings, time ───────────────────────────────────────

/** A heading over the ingredient list. */
export const INGREDIENT_HEADINGS = [
  'ingredients', 'ingredient', "you'll need", 'you’ll need', 'youll need', 'what you need', 'shopping list',
  'ingredienser', 'ingrediens', 'du behöver',
  'zutaten', 'du brauchst', 'einkaufsliste',
  'du trenger', 'du skal bruge', 'ingrédients', 'il vous faut', 'ingredienti', 'ingredientes',
]
/** A heading over the method. */
export const METHOD_HEADINGS = [
  'method', 'instructions', 'instruction', 'directions', 'direction', 'steps', 'how to make it', 'how to make',
  'how to', 'preparation',
  'gör så här', 'så gör du', 'instruktioner', 'tillagning', 'gör så',
  'zubereitung', 'anleitung', "so geht's", 'so geht’s', 'so gehts', 'schritte',
  'fremgangsmåte', 'fremgangsmåde', 'framgangsmåte', 'slik gjør du', 'sådan gør du', 'tilberedning', 'tilberedelse',
  'préparation', 'étapes', 'preparazione', 'procedimento', 'preparación', 'elaboración', 'instrucciones', 'pasos',
]
/** "Step 2", "Steg 2", "Étape 2": the word before a step's number. */
export const STEP_WORDS = ['step', 'steg', 'schritt', 'trinn', 'trin', 'étape', 'etape', 'passo', 'paso']
/** Nutrition facts end a section; nothing under them is a line of the recipe. */
export const NUTRITION_HEADINGS = [
  'nutritional', 'nutrition', 'macros', 'nährwerte', 'makros', 'näringsvärde', 'näringsinnehåll',
  'næringsinnhold', 'næringsindhold', 'valeurs nutritionnelles', 'valori nutrizionali', 'información nutricional',
]
/** "Serves 4", "för 4", "für 4": the word, then how many. */
export const SERVINGS_BEFORE = [
  'serves', 'servings', 'serving', 'portioner', 'portion', 'för', 'für', 'porsjoner', 'portions', 'porzioni',
  'porciones', 'raciones',
]
/** "4 portioner", "4 Personen": how many, then the word. */
export const SERVINGS_AFTER = [
  'portioner', 'portionen', 'portions', 'servings', 'personer', 'personen', 'porsjoner', 'personnes',
  'persone', 'porzioni', 'personas', 'porciones', 'raciones',
]
export const MINUTE_WORDS = [
  'minutes', 'minute', 'minuter', 'minut', 'minuten', 'mins', 'min', 'minutter', 'minutt', 'minuti', 'minuto', 'minutos',
]
export const HOUR_WORDS = [
  'hours', 'hour', 'timmar', 'timme', 'tim', 'stunden', 'stunde', 'std', 'timer', 'time', 'heures', 'heure', 'ore', 'ora',
  'horas', 'hora',
]

// ── the patterns built from the lists ─────────────────────────────────────────

const escape = (w: string) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
/** One alternation, longest first so "how to make it" wins over "how to". */
export const anyOf = (words: string[]) => [...words].sort((a, b) => b.length - a.length).map(escape).join('|')

// `(?!\p{L})` rather than `\b`, which ends a word at a non-ASCII letter ("für").
/** A line that starts with one of the words, as a whole word. */
export const startsWithWord = (words: string[]) => new RegExp(`^(${anyOf(words)})(?!\\p{L})`, 'iu')
